import { useState, useEffect } from "react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { User as UserIcon, Save } from "lucide-react";

const INDUSTRIES = ["Tech","Finance","Healthcare","E-commerce","EdTech","Consulting","Media","Other"];
import { TARGET_ROLES } from "../constants/targetRoles";
const ROLES = TARGET_ROLES;

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: "", mobile: "", current_role: "", years_experience: 0, industry: "", target_role: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "",
      mobile: user.mobile || "",
      current_role: user.current_role || "",
      years_experience: user.years_experience || 0,
      industry: user.industry || "",
      target_role: user.target_role || "",
    });
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ ...form, years_experience: parseFloat(form.years_experience) || 0 });
      toast.success("Profile updated");
    } catch (err) {
      toast.error("Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="profile-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Profile</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Tune your inputs.</h1>
        <p className="text-zinc-600 mt-2 text-sm">The more we know, the better your interviews and skill gap reports get.</p>
      </div>

      <form onSubmit={save} className="card-flat p-6 space-y-4">
        <div className="flex items-center gap-4 pb-4 border-b border-zinc-200">
          <div className="h-14 w-14 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center"><UserIcon size={20} /></div>
          <div>
            <div className="font-display font-bold text-lg">{user?.name}</div>
            <div className="text-sm text-zinc-500">{user?.email}</div>
          </div>
        </div>

        <Field label="Full name" testid="profile-name" value={form.name} onChange={(v)=>setForm({...form,name:v})} />
        <Field label="Mobile (10 digits)" testid="profile-mobile" value={form.mobile} onChange={(v)=>setForm({...form,mobile:v})} placeholder="+91 98765 43210" hint="One mobile number can only be linked to a single account." />
        <Field label="Current role" testid="profile-current-role" value={form.current_role} onChange={(v)=>setForm({...form,current_role:v})} placeholder="e.g. SDE-1" />
        <Field label="Years of experience" testid="profile-years" type="number" value={form.years_experience} onChange={(v)=>setForm({...form,years_experience:v})} />
        <SelectField label="Industry" testid="profile-industry" value={form.industry} onChange={(v)=>setForm({...form,industry:v})} options={["", ...INDUSTRIES]} />
        <SelectField label="Target role" testid="profile-target-role" value={form.target_role} onChange={(v)=>setForm({...form,target_role:v})} options={["", ...ROLES]} />

        <button data-testid="profile-save-btn" disabled={saving} className="mt-2 h-11 px-5 rounded-md bg-[#0E7C66] text-white font-semibold hover:bg-[#0A5E4D] transition flex items-center gap-2">
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, type="text", placeholder, hint }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</label>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e)=>onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:border-[#0E7C66]" />
      {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
    </div>
  );
}
function SelectField({ label, value, onChange, testid, options }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</label>
      <select data-testid={testid} value={value} onChange={(e)=>onChange(e.target.value)} className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md bg-white">
        {options.map(o => <option key={o} value={o}>{o || "—"}</option>)}
      </select>
    </div>
  );
}
